import React, { useState, useEffect, useCallback } from 'react';
import { FixedSizeList as List } from 'react-window';
import debounce from 'lodash/debounce';

const Option = ({ index, style, data }) => {
    const item = data.items[index];
    const isSelected = data.selectedValues.includes(item.cli);

    const onKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            data.toggleSelect(item.cli);
        }
    };

    return (
        <div
            role="option"
            aria-selected={isSelected}
            tabIndex={0}
            style={{
                ...style,
                padding: '8px 12px',
                backgroundColor: isSelected ? '#cce5ff' : 'white',
                borderBottom: '1px solid #eee',
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: isSelected ? 'bold' : 'normal',
            }}
            onClick={() => data.toggleSelect(item.cli)}
            onKeyDown={onKeyDown}
        >
            {item.cli} ({item.dialerName})
        </div>
    );
};

const VirtualizedSelect = ({
    options,
    onChange,
    value = '',
    height = 300,
    width = 442,
    selectedLocations = [],
    isMulti = false,
    debounceTime = 300,
}) => {
    const selectedArray = value
        ? value.split(',').map((v) => v.trim()).filter(Boolean)
        : [];

    const [selectedValues, setSelectedValues] = useState(selectedArray);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    useEffect(() => {
        setSelectedValues(selectedArray);
    }, [value]);

    const debouncedSetSearch = useCallback(
        debounce((term) => {
            setDebouncedSearchTerm(term);
        }, debounceTime),
        [debounceTime]
    );

    useEffect(() => {
        debouncedSetSearch(searchTerm);
        
        return () => {
            debouncedSetSearch.cancel();
        };
    }, [searchTerm, debouncedSetSearch]);

    const searchEnabled = selectedLocations.length > 0;

    const filteredOptions = options?.filter(
        (obj) =>
            obj.cliStatus !== 2 &&
            (selectedLocations.length === 0 || selectedLocations.includes(obj.locationName)) &&

            // Apply search filter if search is enabled and term exists
            (!searchEnabled || !debouncedSearchTerm || (obj.dialerName && obj.dialerName.toLowerCase().includes(debouncedSearchTerm.toLowerCase())))
    );

    const toggleSelect = useCallback((cli) => {
        let next;
        if (isMulti) {
            if (selectedValues.includes(cli)) {
                next = selectedValues.filter((v) => v !== cli);
            } else {
                next = [...selectedValues, cli];
            }
        } else {
            next = selectedValues.includes(cli) ? [] : [cli];
        }

        setSelectedValues(next);
        if (onChange) onChange(next.join(','));
    }, [isMulti, selectedValues, onChange]);

    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value);
    }, []);

    const listHeight = searchEnabled ? height - 40 : height;

    return (
        <div
            style={{
                width,
                border: '1px solid #ccc',
                borderRadius: 4,
                fontFamily: 'sans-serif',
            }}
        >
            {searchEnabled && (
                <div style={{ padding: '8px' }}>
                    <input
                        type="text"
                        placeholder="Search Dialer..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        style={{
                            width: '100%',
                            padding: '6px',
                            boxSizing: 'border-box',
                            border: '1px solid #ddd',
                            borderRadius: 4,
                        }}
                    />
                    {searchTerm !== debouncedSearchTerm && (
                        <div style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            marginTop: '4px',
                            fontStyle: 'italic' 
                        }}>
                            Searching...
                        </div>
                    )}
                </div>
            )}
            
            {!filteredOptions.length ? (
                <div
                    style={{
                        width,
                        height: listHeight,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'sans-serif',
                        color: '#666',
                        fontStyle: 'italic',
                        userSelect: 'none',
                    }}
                >
                    {debouncedSearchTerm ? 'No CLI matches your search' : 'No CLI found'}
                </div>
            ) : (
                <List
                    height={listHeight}
                    itemCount={filteredOptions.length}
                    itemSize={35}
                    width={width}
                    itemData={{
                        items: filteredOptions,
                        selectedValues,
                        toggleSelect,
                    }}
                >
                    {Option}
                </List>
            )}
        </div>
    );
};

export default VirtualizedSelect;